import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const SalaryInformation = () => {
  const [showSalaryDetails, setShowSalaryDetails] = useState(false);
  const [passwordVerification, setPasswordVerification] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const salaryData = {
    currentMonth: 'November 2024',
    grossSalary: 5500.00,
    basicSalary: 3300.00,
    allowances: {
      hra: 1320.00,
      transport: 500.00,
      medical: 380.00
    },
    deductions: {
      tax: 825.00,
      insurance: 165.00,
      providentFund: 330.00
    },
    netSalary: 4180.00,
    ytdGross: 60500.00,
    ytdNet: 45980.00
  };

  const payStubs = [
    {
      id: 1,
      month: 'November 2024',
      netPay: 4180.00,
      status: 'Processed',
      payDate: '2024-11-30',
      downloadUrl: '#'
    },
    {
      id: 2,
      month: 'October 2024',
      netPay: 4180.00,
      status: 'Paid',
      payDate: '2024-10-31',
      downloadUrl: '#'
    },
    {
      id: 3,
      month: 'September 2024',
      netPay: 4180.00,
      status: 'Paid',
      payDate: '2024-09-30',
      downloadUrl: '#'
    }
  ];

  const handlePasswordSubmit = () => {
    // Mock password verification - in real app, this would be secure
    if (password === 'employee123') {
      setPasswordVerification(true);
      setShowSalaryDetails(true);
      setPasswordError('');
      setPassword('');
    } else {
      setPasswordError('Invalid password. Use: employee123');
    }
  };

  const handleViewSalary = () => {
    if (!passwordVerification) {
      setShowSalaryDetails(true);
    } else {
      setShowSalaryDetails(!showSalaryDetails);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    })?.format(amount);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return 'text-success bg-success/10 border-success/20';
      case 'processed':
        return 'text-primary bg-primary/10 border-primary/20';
      case 'pending':
        return 'text-warning bg-warning/10 border-warning/20';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border card-shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Salary Information</h3>
        <div className="flex items-center space-x-2">
          <Icon name="Shield" size={16} color="var(--color-success)" />
          <span className="text-xs text-success">Secure Access</span>
        </div>
      </div>
      {!showSalaryDetails ? (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Lock" size={24} color="var(--color-primary)" />
          </div>
          <h4 className="font-medium text-foreground mb-2">Secure Salary Access</h4>
          <p className="text-sm text-muted-foreground mb-4">
            Enter your password to view salary details and download pay stubs
          </p>
          
          <div className="max-w-xs mx-auto space-y-3">
            <Input
              type="password"
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e?.target?.value)}
              error={passwordError}
            />
            <Button 
              onClick={handlePasswordSubmit}
              disabled={!password}
              className="w-full"
            >
              <Icon name="Unlock" size={16} className="mr-2" />
              Access Salary Information
            </Button>
          </div>
          
          <p className="text-xs text-muted-foreground mt-4">
            Mock credentials: employee123
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Current Month Summary */}
          <div className="bg-primary/5 rounded-lg p-4 border border-primary/10">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-foreground">{salaryData?.currentMonth}</h4>
              <Button variant="ghost" size="sm" onClick={() => setShowSalaryDetails(false)}>
                <Icon name="EyeOff" size={16} />
              </Button>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Gross Salary</p>
                <p className="text-lg font-semibold text-foreground">
                  {formatCurrency(salaryData?.grossSalary)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Net Salary</p>
                <p className="text-lg font-semibold text-primary">
                  {formatCurrency(salaryData?.netSalary)}
                </p>
              </div>
            </div>
          </div>

          {/* Salary Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Earnings */}
            <div className="bg-success/5 rounded-lg p-4 border border-success/10">
              <h5 className="font-medium text-foreground mb-3 flex items-center">
                <Icon name="Plus" size={16} color="var(--color-success)" className="mr-2" />
                Earnings
              </h5>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Basic Salary</span>
                  <span className="font-medium">{formatCurrency(salaryData?.basicSalary)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">HRA</span>
                  <span className="font-medium">{formatCurrency(salaryData?.allowances?.hra)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Transport</span>
                  <span className="font-medium">{formatCurrency(salaryData?.allowances?.transport)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Medical</span>
                  <span className="font-medium">{formatCurrency(salaryData?.allowances?.medical)}</span>
                </div>
              </div>
            </div>

            {/* Deductions */}
            <div className="bg-error/5 rounded-lg p-4 border border-error/10">
              <h5 className="font-medium text-foreground mb-3 flex items-center">
                <Icon name="Minus" size={16} color="var(--color-error)" className="mr-2" />
                Deductions
              </h5>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Income Tax</span>
                  <span className="font-medium">{formatCurrency(salaryData?.deductions?.tax)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Insurance</span>
                  <span className="font-medium">{formatCurrency(salaryData?.deductions?.insurance)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Provident Fund</span>
                  <span className="font-medium">{formatCurrency(salaryData?.deductions?.providentFund)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* YTD Summary */}
          <div className="bg-accent/5 rounded-lg p-4 border border-accent/10">
            <h5 className="font-medium text-foreground mb-3">Year-to-Date Summary</h5>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">YTD Gross</p>
                <p className="text-lg font-semibold text-foreground">
                  {formatCurrency(salaryData?.ytdGross)}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">YTD Net</p>
                <p className="text-lg font-semibold text-accent">
                  {formatCurrency(salaryData?.ytdNet)}
                </p>
              </div>
            </div>
          </div>

          {/* Pay Stubs */}
          <div>
            <h5 className="font-medium text-foreground mb-3">Recent Pay Stubs</h5>
            <div className="space-y-2">
              {payStubs?.map((stub) => (
                <div key={stub?.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border/50">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name="FileText" size={16} color="var(--color-primary)" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{stub?.month}</p>
                      <p className="text-sm text-muted-foreground">
                        Net Pay: {formatCurrency(stub?.netPay)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(stub?.status)}`}>
                      {stub?.status}
                    </span>
                    <Button variant="ghost" size="sm">
                      <Icon name="Download" size={16} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalaryInformation;